import { useFormsTemplate } from '@commenda/api';
import { Icons, Loading } from '@commenda/kakashi';
import { DSLFormRenderer } from '@commenda/ui/form-builder/hooks/useFormRenderer';
import { useRouter } from 'next/router';
import { useMemo, type ComponentProps } from 'react';
import AffiliateClientPicker from '../../affiliate-ai/AffiliateClientPicker';
import { useAffiliateAiClient } from '../../affiliate-ai/useAffiliateAiClient';
import { AffiliateFormHelper } from './affiliate-form-helper';

type FormDSL = ComponentProps<typeof DSLFormRenderer>['dsl'];

const PreviewMessage = ({
  title,
  detail,
}: {
  title: string;
  detail: string;
}) => (
  <div className="h-screen flex flex-col items-center justify-center bg-bg-tertiary gap-4">
    <Icons.AlertCircle className="w-12 h-12 text-text-error" />
    <h2 className="txt-large-medium text-text-primary">{title}</h2>
    <p className="txt-medium-regular text-text-tertiary">{detail}</p>
  </div>
);

/** Previewing a form is scoped to one of the firm's own clients, so entity and key-person fields
 * resolve against real data. Every read is authorized per company server-side, so the picker's list
 * is a convenience and not the boundary. With no client picked the fields render waiting, which is
 * how the form behaves before its workflow supplies them. */
export const AffiliateFormPreview = () => {
  const router = useRouter();
  const firmId = typeof router.query.id === 'string' ? router.query.id : '';
  const templateId =
    typeof router.query.templateId === 'string'
      ? router.query.templateId
      : undefined;

  const {
    template,
    isLoading,
    error: templateError,
  } = useFormsTemplate(templateId);
  const { clients, clientId, setClientId } = useAffiliateAiClient();

  const dsl = useMemo(
    () => (template?.dsl ? (template.dsl as unknown as FormDSL) : null),
    [template]
  );

  if (!firmId || !templateId) {
    return (
      <div className="h-screen flex items-center justify-center bg-bg-tertiary">
        <Loading />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-bg-tertiary">
        <Loading />
      </div>
    );
  }

  if (templateError || !template) {
    return (
      <PreviewMessage
        title={templateError ? 'Failed to load form' : 'Form not found'}
        detail="The form could not be found, or there was an error loading it."
      />
    );
  }

  if (!template.key) {
    return (
      <PreviewMessage
        title="Form configuration error"
        detail="This form is missing a key and cannot accept submissions."
      />
    );
  }

  if (!dsl) return null;

  const backToEditor = () => {
    router.push(AffiliateFormHelper.getFormEditorHref(firmId, template.id));
  };

  return (
    <div className="flex flex-col bg-bg-light min-w-screen min-h-screen">
      <div className="h-full w-full max-w-screen-2xl mx-auto">
        <div className="flex items-center justify-end gap-sm px-xl pt-xl">
          <span className="txt-small-medium text-text-tertiary">
            Preview as client
          </span>
          <AffiliateClientPicker
            clients={clients}
            clientId={clientId}
            onClientChange={setClientId}
          />
        </div>
        <DSLFormRenderer
          dsl={dsl}
          dslContext={clientId === null ? {} : { companyId: clientId }}
          formTemplateId={template.id}
          formTemplateKey={template.key}
          onSubmitSuccess={backToEditor}
          onSaveSuccess={backToEditor}
          onClose={backToEditor}
          isPreview
        />
      </div>
    </div>
  );
};
